'use client';

import { Export } from '@tiptap-pro/extension-export';
import Bold from '@tiptap/extension-bold';
import BulletList from '@tiptap/extension-bullet-list';
import Document from '@tiptap/extension-document';
import Dropcursor from '@tiptap/extension-dropcursor';
import Heading from '@tiptap/extension-heading';
import Highlight from '@tiptap/extension-highlight';
import History from '@tiptap/extension-history';
import Image from '@tiptap/extension-image';
import Italic from '@tiptap/extension-italic';
import Link from '@tiptap/extension-link';
import ListItem from '@tiptap/extension-list-item';
import OrderedList from '@tiptap/extension-ordered-list';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import { EditorContent, useEditor } from '@tiptap/react';

import { Button, Modal, Typography } from 'components';
import { CloseIcon } from 'lib/svgs/Icons';
import { useTranslations } from 'next-intl';
import { Dispatch, FC, SetStateAction, useRef } from 'react';
import { cn } from 'utils/cn';
import MenuBar from './MenuBar';
import MenuContentGeneration from './MenuContentGeneration';

import IndentExtension from 'extensions/IndentExtension';
import { GeneratedContent } from 'gql/graphql';
import { useFeatureFlags } from 'providers/ClientProviders/useFeatureFlags';
import tc from 'thousands-counter';
import { ContentGenerationProvider } from './_context/ContentGenerationContext';
import ImprovingMenu from './ImprovingMenu';
import LanguagesSelector from './LanguagesSelector';
import LoadingContent from './LoadingContent';
import './styles.scss';

interface IModalContentGeneration {
  open: boolean;
  defaultGeneratedContent?: GeneratedContent;
  onCancel: () => void;
  onReset?: () => void;
  refetch?: () => void;
  copyGeneratedContent?: Partial<GeneratedContent>;
  setCopyGeneratedContent?: Dispatch<SetStateAction<Partial<GeneratedContent>>>;
}

const ModalContentGeneration: FC<IModalContentGeneration> = ({
  open,
  defaultGeneratedContent,
  onCancel,
  onReset,
  refetch,
  copyGeneratedContent,
  setCopyGeneratedContent
}) => {
  const t = useTranslations();
  const ref = useRef<HTMLDivElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const { data: featureFlags } = useFeatureFlags();

  const editor = useEditor({
    extensions: [
      Document,
      Paragraph,
      Text,
      History,
      IndentExtension,
      Heading,
      TextAlign.configure({
        types: ['heading', 'paragraph']
      }),
      Bold,
      Highlight,
      Italic,
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: 'https'
      }),
      BulletList,
      ListItem,
      OrderedList,
      Image,
      Dropcursor,
      Export.configure({
        appId: process.env.NEXT_PUBLIC_TIPTAP_APP_ID,
        token: process.env.NEXT_PUBLIC_TIPTAP_JWT_TOKEN
      })
    ],
    content: ``,
    editorProps: {}
  });

  const handleCancel = () => {
    onCancel();
    refetch?.();
  };

  return (
    <Modal
      zDialog={cn('!z-[1999]')}
      zDialogBackdrop="z-[1000]"
      className={'min-h-[90vh] w-full'}
      onClose={handleCancel}
      open={open}
    >
      <ContentGenerationProvider
        defaultGeneratedContent={defaultGeneratedContent}
        copyGeneratedContent={copyGeneratedContent}
        setCopyGeneratedContent={setCopyGeneratedContent}
        editor={editor}
        open={open}
        refetch={refetch}
      >
        {({ title, handleTitle, generatedContent, isPending, words }) => (
          <div
            className={cn(
              'overflow-y-auto',
              'relative flex w-full flex-col rounded bg-white shadow-custom'
            )}
          >
            <div className="absolute right-2 top-2 z-[1000]">
              <Button
                size="small"
                variant="icon"
                color="neutral"
                className="!w-8 rounded-full"
                onClick={handleCancel}
                startAdornment={<CloseIcon />}
              />
            </div>
            <div className="flex h-14 flex-col justify-center pl-6">
              <input
                className={cn(
                  'input-title-editable',
                  'focus:!ring-none mt-1 !border-none !outline-none !ring-0 focus:!border-none focus:!outline-none',
                  '!w-[calc(100%_-_50px)] !pl-0 !text-3xl',
                  '!font-medium placeholder:!font-medium'
                )}
                placeholder={t('placeholders.untitled')}
                value={title || ''}
                onChange={handleTitle}
                // noClose
              />
            </div>
            <div className={cn('flex h-fit flex-1 bg-tertiary-900', 'overflow-y-scroll')} ref={ref}>
              <div className="flex flex-1 flex-col overflow-y-scroll">
                <div className="sticky top-0 z-[100] shadow-[0_8px_8px_-8px_rgba(36,92,144,0.17)]">
                  <div className="relative bg-white p-4 !pb-2 shadow-[0_8px_8px_-8px_rgba(36,92,144,0.17)_inset]">
                    <MenuBar editor={editor} />
                  </div>
                </div>
                <div className="flex flex-1 flex-col overflow-auto">
                  <div className="flex flex-1 flex-col overflow-auto p-6 pb-0 pt-2">
                    <div className="mb-2 flex !h-6 justify-between">
                      <LanguagesSelector />
                      <div className="flex justify-end">
                        <Typography
                          variant="light5"
                          className="font- !mb-0 text-center text-tertiary-200 "
                        >
                          {t('content_generation.total_words')}:{' '}
                          <strong>
                            {new Intl.NumberFormat()
                              .format(generatedContent?.words ?? 0)
                              .replaceAll(',', ' ')}
                          </strong>
                        </Typography>
                      </div>
                    </div>
                    {words === 0 && !isPending && (
                      <div className="relative">
                        <Typography
                          variant="light3"
                          className="absolute left-4 top-4 !mb-0 select-none text-center text-tertiary-400 "
                        >
                          {t('placeholders.content_generation')}
                        </Typography>
                      </div>
                    )}
                    {isPending && (
                      <div className="relative">
                        <div className="absolute left-4 top-6">
                          <LoadingContent />
                        </div>
                      </div>
                    )}
                    {featureFlags?.improveContentGenerationEnabled && (
                      <ImprovingMenu editor={editor} />
                    )}
                    <EditorContent
                      editor={editor}
                      ref={editorRef}
                      className={cn(
                        'editor-content-generation h-full max-h-[calc(93vh_-_202px)]',
                        'overflow-scroll rounded rounded-b-none bg-white p-4 shadow-custom !outline-none',
                        isPending ? 'loading' : '',
                        words === 0 ? 'blocked' : ''
                      )}
                    />
                    <div className="bg-white pl-4 shadow-custom-x">
                      <Typography
                        variant="light5"
                        className="font- !mb-0 text-center text-tertiary-200 "
                      >
                        {t('content_generation.token_usage')}:{' '}
                        <strong>{tc(generatedContent?.tokens ?? 0, { digits: 2 })}</strong>
                      </Typography>
                    </div>
                    <div></div>
                  </div>
                </div>
              </div>
              <div className="flex w-[20rem] shrink-0 flex-col">
                <MenuContentGeneration onCancel={handleCancel} onReset={onReset} />
              </div>
            </div>
          </div>
        )}
      </ContentGenerationProvider>
    </Modal>
  );
};

export default ModalContentGeneration;
